import type {DefaultError} from "@tanstack/react-query";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {useCustomQuery} from "~/query/queryClient.ts";
import type {ClContract, ClContractGen, ClUnmatchedGen} from "./contractLinkageData";

/** `GET /api/showcase/contract_linkage/cl_contracts/` */
export const CL_CONTRACTS_URL = "/showcase/contract_linkage/cl_contracts/";

/** `GET /api/showcase/contract_linkage/contract_gens/` */
export const CL_CONTRACT_GENS_URL = "/showcase/contract_linkage/contract_gens/";

/** `GET /api/showcase/contract_linkage/unmatched_gens/` */
export const CL_UNMATCHED_GENS_URL = "/showcase/contract_linkage/unmatched_gens/";

export const CL_CONTRACTS_QUERY_KEY = [CL_CONTRACTS_URL] as const satisfies UrlQueryKey;
export const CL_CONTRACT_GENS_QUERY_KEY = [CL_CONTRACT_GENS_URL] as const satisfies UrlQueryKey;
export const CL_UNMATCHED_GENS_QUERY_KEY = [CL_UNMATCHED_GENS_URL] as const satisfies UrlQueryKey;

type ContractsResponse<T> = {contracts: T[]};

/** Contract catalogue for linkage review. */
export function useClContractsQuery() {
    return useCustomQuery<
        ContractsResponse<ClContract>,
        DefaultError,
        ClContract[],
        typeof CL_CONTRACTS_QUERY_KEY
    >({
        queryKey: CL_CONTRACTS_QUERY_KEY,
        select: data => data.contracts ?? [],
    });
}

/** Generator recipes for AI-matched transactions. */
export function useClContractGensQuery() {
    return useCustomQuery<
        ContractsResponse<ClContractGen>,
        DefaultError,
        ClContractGen[],
        typeof CL_CONTRACT_GENS_QUERY_KEY
    >({
        queryKey: CL_CONTRACT_GENS_QUERY_KEY,
        select: data => data.contracts ?? [],
    });
}

/** Generator recipes for unmatched transactions. */
export function useClUnmatchedGensQuery() {
    return useCustomQuery<
        ContractsResponse<ClUnmatchedGen>,
        DefaultError,
        ClUnmatchedGen[],
        typeof CL_UNMATCHED_GENS_QUERY_KEY
    >({
        queryKey: CL_UNMATCHED_GENS_QUERY_KEY,
        select: data => data.contracts ?? [],
    });
}
